import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Track } from '@app/core/models/Track';
import { PlayAudio, Resume, Stop } from '@app/core/states/audio.actions';
import { RadioState } from '@app/core/states/radio.state';
import { ToggleSelectTrack } from '@app/library/states/selected-tracks.actions';
import { SelectedTracksState } from '@app/library/states/selected-tracks.state';
import { platformCmdOrCtrlKey } from '@app/shared/utils';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { ContextMenuComponent, ContextMenuService } from '@perfectmemory/ngx-contextmenu';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ColumnShown } from '../../../models/column-shown.model';

@Component({
  selector: 'rk-library-audio-line',
  templateUrl: './library-audio-line.component.html',
  styleUrls: ['./library-audio-line.component.scss'],
})
export class LibraryAudioLineComponent {
  @ViewChild(MatMenuTrigger) tagList: MatMenuTrigger;

  @ViewChild('tagRef') tagZone: ElementRef;

  @ViewChild('playlistRef') playlistZone: ElementRef;

  @Input()
  set isContextMenuOpen(isOpen: boolean) {
    if (this.hasContextMenuOpen && !isOpen) {
      this.hasContextMenuOpen = false;
    }
  }

  @Input()
  set track(inTrack: Track) {
    this._track = inTrack;
    this.checked$ = this.store
      .select(SelectedTracksState.isTrackSelected)
      .pipe(map(fn => fn(inTrack)));
  }

  get track(): Track {
    return this._track;
  }

  _track: Track;

  @Input()
  tagDisplayedLimiter: number;

  @Input()
  currentBacName: string;

  @Input()
  isPlaying = false;

  @Input()
  isLoaded = false;

  @Output()
  readonly editTrack = new EventEmitter();

  @Input()
  hasPaddingRight = false;

  @Input()
  columns: ColumnShown;

  @Select(SelectedTracksState.numberTracksSelected)
  numberCheckedTracks$: Observable<number>;

  @Output()
  readonly shiftClicked = new EventEmitter<number>();

  checked$: Observable<boolean>;

  // Optional
  @Input() contextMenu: ContextMenuComponent;

  @Input()
  isChronic = false;

  @Input()
  isTag = false;

  @Input()
  linePlanLocked: boolean;

  hasContextMenuOpen = false;

  constructor(
    private readonly contextMenuService: ContextMenuService,
    private readonly store: Store,
  ) {}

  selectLine() {
    this.store.dispatch(new ToggleSelectTrack(this.track, true));
  }

  getType(): string {
    if (this.isChronic) {
      return 'chronic';
    }

    return this.isTag ? 'tag' : 'track';
  }

  onSelectClickLine(event: MouseEvent) {
    if (platformCmdOrCtrlKey(event)) {
      this.store.dispatch(new ToggleSelectTrack(this.track));

      return;
    }
    if (event.shiftKey) {
      this.shiftClicked.emit(this.track.id);
      event.stopPropagation();
      event.preventDefault();
    }
  }

  @Dispatch()
  playTrack(track: Track) {
    if (this.isPlaying && this.isLoaded) {
      return new Stop();
    }
    if (this.isLoaded) {
      return new Resume();
    }

    return new PlayAudio(track);
  }

  onContextMenu($event: MouseEvent, item: any): void {
    this.hasContextMenuOpen = true;
    this.contextMenuService.show.next({
      item,
      contextMenu: this.contextMenu,
      event: $event,
    });
    $event.preventDefault();
    $event.stopPropagation();
  }

  dndDragImageOffsetFunction(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  @Dispatch()
  openTag(id: number) {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    return new Navigate(['radio', radioId, 'media', 'tag', id], {}, { replaceUrl: true });
  }

  @Dispatch()
  openPlaylist(id: number) {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    return new Navigate(
      ['radio', radioId, 'media', 'playlist', id],
      {},
      { replaceUrl: true },
    );
  }
}
